import React, { useEffect, useState } from 'react';
import { Button, ConfirmationModal, Modal, ModalContent, ModalCloseButton, FormField, Input } from '../common';
import { assignEmailToGuest, EXISTING_MEMBER_NO_GAMES } from '../../services/guestService';
import { assignEmailAndMergeExistingMember } from '../../services/guestAccountLinkingService';

const AssignEmailModal = ({
  isOpen,
  onClose,
  onEmailAssigned,
  guest,
  currentUser,
  clubId = null
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [pendingMerge, setPendingMerge] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setError(null);
      setPendingMerge(null);
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen || !guest) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updatedGuest = await assignEmailToGuest(
        guest.id,
        email.trim().toLowerCase(),
        currentUser.id,
        clubId
      );

      setEmail('');
      onEmailAssigned(updatedGuest);
      onClose();
    } catch (err) {
      if (err.code === EXISTING_MEMBER_NO_GAMES) {
        setPendingMerge({
          memberId: err.memberId,
          memberName: err.memberName,
          email: email.trim().toLowerCase(),
        });
        return;
      }
      console.error('Error assigning email:', err);
      setError(err.message || 'Failed to assign email');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMerge = async () => {
    if (!pendingMerge) return;

    setLoading(true);
    setError(null);

    try {
      const result = await assignEmailAndMergeExistingMember({
        guestId: guest.id,
        email: pendingMerge.email,
        assignedBy: currentUser.id,
        clubId,
        memberId: pendingMerge.memberId,
      });

      setPendingMerge(null);
      setEmail('');
      onEmailAssigned(result.guest);
      onClose();
    } catch (err) {
      console.error('Error merging guest into member:', err);
      setPendingMerge(null);
      setError(err.message || 'Failed to merge guest account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent size="small">
          <ModalCloseButton onClose={onClose} />
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Assign Email to Guest
          </h3>

          <div className="p-6">
            {/* Guest Info - uses design system surfaces */}
            <div className="mb-6 p-4 bg-surface-subtle border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 h-12 w-12 rounded-full bg-surface-elevated border border-border flex items-center justify-center">
                  <span className="text-lg font-medium text-text-primary">
                    {guest.first_name?.[0]}{guest.last_name?.[0]}
                  </span>
                </div>
                <div className="min-w-0">
                  <h4 className="text-lg font-medium text-text-primary truncate">
                    {guest.first_name} {guest.last_name}
                  </h4>
                  <div className="flex items-center space-x-2 flex-wrap gap-1">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info-light border border-info text-text-primary">
                      👤 Guest
                    </span>
                    <span className="text-sm text-text-muted">
                      {guest.gender}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box - uses design system info colors */}
            <div className="mb-6 p-4 bg-info-light border border-info rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-info" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-text-primary">
                    Email Assignment
                  </h3>
                  <p className="mt-1 text-sm text-text-secondary">
                    Assigning an email will allow this guest to claim their game history
                    when they create an account with this email address.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <FormField
                label="Email Address"
                required
                error={error}
                helperText="The guest can use this email to claim their account and game history"
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter guest's email address"
                  disabled={loading}
                  error={!!error}
                  autoFocus
                />
              </FormField>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  onClick={onClose}
                  variant="secondary"
                  size="small"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                >
                  {loading ? 'Assigning...' : 'Assign Email'}
                </Button>
              </div>
            </form>
          </div>
        </ModalContent>
      </Modal>

      <ConfirmationModal
        isOpen={Boolean(pendingMerge)}
        onClose={() => setPendingMerge(null)}
        onConfirm={handleConfirmMerge}
        isDangerous
        loading={loading}
        title="Merge guest into member"
        confirmText={pendingMerge ? `Merge into ${pendingMerge.memberName}` : 'Merge'}
        message={
          pendingMerge
            ? `${pendingMerge.memberName} is already a member of this club and has not played here. Confirming merges this guest's games, ratings, and partnerships into their account. This cannot be undone.`
            : ''
        }
      />
    </>
  );
};

export default AssignEmailModal;
